import { useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';

import { accessTokenStorage } from '../storage/auth/index';
import { selectedCompanyStorage } from '../storage/company/selected-company.util';
import { getScenarioSocketUrl } from '../utils/global.util';
import { SOCKET_IO_CLIENT_BASE_OPTIONS } from './socket-io-client-options';
import { ensureFreshAccessTokenForWs } from './ws-ensure-access-token.util';

type WsState = {
  emit: (key: string, ...args: unknown[]) => void;
  getSocket: () => Socket;
  reconnect: (force?: boolean) => void;
  socket: null | Socket;
};

const createSocket = () => {
  const socket = io(getScenarioSocketUrl('scenario-modeling'), {
    ...SOCKET_IO_CLIENT_BASE_OPTIONS,
    autoConnect: false,
    auth: (cb) => {
      void ensureFreshAccessTokenForWs().finally(() => {
        cb({ accessToken: accessTokenStorage.getState().get() || '' });
      });
    },
  });

  socket.on('connect_error', (err) => console.error('WS connect_error:', err));
  socket.on('error', (err) => console.error('WS error:', err));
  socket.on('connect', () => console.log('Scenario ws connected'));
  socket.on('disconnect', (reason) => console.log('Scenario ws disconnect', reason));

  return socket;
};

export const scenarioSocketStore = create<WsState>()((set, get) => ({
  socket: null,
  getSocket: () => {
    let socket = get().socket;
    if (!socket) {
      socket = createSocket();
      set({ socket: socket });
    }

    if (!socket.connected) socket.connect();
    return socket;
  },

  reconnect: (force = false) => {
    const socket = get().socket;
    if (!socket) return;
    if (force && socket.connected) {
      socket.disconnect();
    }
    if (!socket.connected) {
      socket.connect();
    }
  },

  emit: (key, ...args) => {
    const socket = get().getSocket();

    socket.emit(key, ...args);
  },
}));

const updateSocketAuth = () => {
  const { socket, reconnect } = scenarioSocketStore.getState();
  if (!socket) return;
  if (!socket.connected) {
    reconnect();
  }
};

const reconnectWithCompany = () => {
  const { socket, reconnect } = scenarioSocketStore.getState();
  if (!socket) return;
  reconnect(true);
};

accessTokenStorage.subscribe((state, prev) => {
  if (state.value === prev?.value) return;
  updateSocketAuth();
});

selectedCompanyStorage.subscribe((state, prev) => {
  if (!state.companyId || state.companyId === prev?.companyId) return;
  reconnectWithCompany();
});

export const useScenarioListener = <T>(key: string, callback: (payload: T) => void) => {
  const socket = scenarioSocketStore(({ socket }) => socket);

  useEffect(() => {
    socket?.on(key, callback);
    return () => {
      socket?.off(key, callback);
    };
  }, [callback, key, socket]);
};
